/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn } from '@fluentui/react/lib/DetailsList';
import { Icon, IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { NameCellLink } from 'src/core/components/NameCellLink';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getFormattedDate } from 'src/core/utils/dateFormatter';
import { LuisAppStore } from 'src/luis/core/stores/LuisAppStore';
import { LuisId } from 'src/luis/types/Resource';

export type ConversationApplicationsTableProps = TableProps<LuisAppStore> & {
    /**
     * Function that gets invoked when an app is clicked.
     */
    onAppClick: (appId: LuisId) => void;
};

const messages = defineMessages({
    nameColumnHeader: {
        id: 'luis.conversationApplicationsTable.nameColumnHeader',
        defaultMessage: 'Name'
    },
    cultureColumnHeader: {
        id: 'luis.conversationApplicationsTable.cultureColumnHeader',
        defaultMessage: 'Culture'
    },
    endpointHitsCountColumnHeader: {
        id: 'luis.conversationApplicationsTable.endpointHitsCountColumnHeader',
        defaultMessage: 'Endpoint hits'
    },
    modifiedDateColumnHeader: {
        id: 'luis.conversationApplicationsTable.modifiedDateColumnHeader',
        defaultMessage: 'Last modified'
    }
});

const tableTheme = getScopedTheme('ConversationApplicationsTable');

const iconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({ root: { color: tableTheme.appTypeIconColor, fontSize: '16px' } });

/**
 * Displays a table of all applications.
 *
 * @param props The input properties for the table.
 */
export const ConversationApplicationsTable = (props: ConversationApplicationsTableProps) => {
    const { selection, items, onAppClick, sortKey, ...rest } = props;
    const { localize } = useLocalization();

    const columns: IColumn[] = [
        {
            key: 'name',
            minWidth: 400,
            fieldName: 'name',
            isRowHeader: true,
            isResizable: true,
            isSorted: sortKey?.key === 'name',
            name: localize(messages.nameColumnHeader),
            isSortedDescending: sortKey?.key === 'name' && sortKey?.order === 'desc',
            onRender: (item: LuisAppStore) => (
                <Stack verticalAlign="center" horizontal tokens={{ childrenGap: 20 }}>
                    <Icon iconName="Chat" styles={iconStyles} />
                    <NameCellLink onClick={() => onAppClick(item.id)}>{item.name}</NameCellLink>
                </Stack>
            )
        },
        {
            minWidth: 200,
            isResizable: true,
            key: 'modifiedDate',
            fieldName: 'modifiedDate',
            isSorted: sortKey?.key === 'modifiedDate',
            name: localize(messages.modifiedDateColumnHeader),
            isSortedDescending: sortKey?.key === 'modifiedDate' && sortKey?.order === 'desc',
            onRender: (item: LuisAppStore) => (
                <LuisText variant="medium">{item.modifiedDate ? getFormattedDate(item.modifiedDate) : '-'}</LuisText>
            )
        },
        {
            minWidth: 200,
            key: 'culture',
            isResizable: true,
            fieldName: 'culture',
            isSorted: sortKey?.key === 'culture',
            name: localize(messages.cultureColumnHeader),
            isSortedDescending: sortKey?.key === 'culture' && sortKey?.order === 'desc'
        },
        {
            minWidth: 200,
            isResizable: true,
            key: 'endpointHitsCount',
            fieldName: 'endpointHitsCount',
            isSorted: sortKey?.key === 'endpointHitsCount',
            name: localize(messages.endpointHitsCountColumnHeader),
            isSortedDescending: sortKey?.key === 'endpointHitsCount' && sortKey?.order === 'desc'
        }
    ];

    return <Table<LuisAppStore> items={items} columns={columns} selection={selection} {...rest} />;
};
